import { apiRequest } from '../api/client';

/** Matches `apps/api` `MenuCalendarPositionSummary` (`menu-calendar.repository.ts`). */
export interface MenuCalendarPositionSummary {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  serviceTypeId: string;
}

/** Matches `apps/api` `StoredMenuCalendarEntry` (`menu-calendar.repository.ts`). */
export interface MenuCalendarEntry {
  id: string;
  serviceDate: string;
  menuId: string;
  positionId: string;
  position: MenuCalendarPositionSummary;
  maxQuantity: number | null;
  reservedQuantity: number;
  isPublished: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarSearchParams {
  from: string;
  to: string;
  menuId?: string | undefined;
  positionId?: string | undefined;
  isPublished?: boolean | undefined;
}

/**
 * Matches `apps/api` `GET /calendar` (`searchCalendarQuerySchema`). No
 * `page`/`pageSize`: the API caps the request by date range width (62 days,
 * `422 DATE_RANGE_TOO_WIDE`) instead of paginating rows, same reasoning
 * `design.md` §5 gives — a calendar view is navigated by date, not pages.
 */
export function listCalendarEntries(params: CalendarSearchParams): Promise<MenuCalendarEntry[]> {
  const query = new URLSearchParams({ from: params.from, to: params.to });

  if (params.menuId !== undefined) {
    query.set('menuId', params.menuId);
  }
  if (params.positionId !== undefined) {
    query.set('positionId', params.positionId);
  }
  if (params.isPublished !== undefined) {
    query.set('isPublished', String(params.isPublished));
  }

  return apiRequest<MenuCalendarEntry[]>(`/calendar?${query.toString()}`);
}

/** Matches `apps/api` `createMenuCalendarEntrySchema` (`menu-calendar.dto.ts`). */
export interface MenuCalendarEntryCreateInput {
  serviceDate: string;
  menuId: string;
  positionId: string;
  maxQuantity?: number | null;
  notes?: string | null;
}

/** Matches `apps/api` `POST /calendar` (`menu-calendar.controller.ts`). */
export function createMenuCalendarEntry(
  input: MenuCalendarEntryCreateInput,
): Promise<MenuCalendarEntry> {
  return apiRequest<MenuCalendarEntry>('/calendar', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Matches `apps/api` `updateMenuCalendarEntrySchema`: `serviceDate`/`menuId`
 * are intentionally absent, both immutable once the entry exists
 * (`menu-calendar/spec.md` → "Edición de entrada de calendario").
 */
export interface MenuCalendarEntryUpdateInput {
  maxQuantity?: number | null;
  notes?: string | null;
}

/** Matches `apps/api` `PATCH /calendar/:id`. */
export function updateMenuCalendarEntry(
  id: string,
  input: MenuCalendarEntryUpdateInput,
): Promise<MenuCalendarEntry> {
  return apiRequest<MenuCalendarEntry>(`/calendar/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `DELETE /calendar/:id`. */
export function deleteMenuCalendarEntry(id: string): Promise<void> {
  return apiRequest<void>(`/calendar/${id}`, { method: 'DELETE' });
}

export interface PublishMenuCalendarEntryResult {
  entry: MenuCalendarEntry;
  /** `MISSING_SIN_TACC` / `MISSING_VEGANO`; informative only, never blocks the publish (`menu-calendar/spec.md` → "Publicación explícita"). */
  dietaryWarnings: string[];
}

/** Matches `apps/api` `PATCH /calendar/:id/publish`. Same endpoint drives both directions of `isPublished`. */
export function publishMenuCalendarEntry(
  id: string,
  isPublished: boolean,
): Promise<PublishMenuCalendarEntryResult> {
  return apiRequest<PublishMenuCalendarEntryResult>(`/calendar/${id}/publish`, {
    method: 'PATCH',
    body: JSON.stringify({ isPublished }),
  });
}

export interface DietaryCoverageEntry {
  code: 'SIN_TACC' | 'VEGANO';
  covered: boolean;
}

export interface DietarySummaryEntry {
  date: string;
  coverage: DietaryCoverageEntry[];
}

/** Matches `apps/api` `GET /calendar/dietary-summary` (`get-dietary-summary.use-case.ts`). One entry per day in `[from, to]`, even a day with zero calendar entries. */
export function getDietarySummary(from: string, to: string): Promise<DietarySummaryEntry[]> {
  const query = new URLSearchParams({ from, to });

  return apiRequest<DietarySummaryEntry[]>(`/calendar/dietary-summary?${query.toString()}`);
}

export interface CopyWeekPreviewInput {
  sourceFrom: string;
  sourceTo: string;
  targetOffsetDays: number;
}

export interface CopyPlanRow {
  sourceEntryId: string;
  menuId: string;
  positionId: string;
  sourceDate: string;
  targetDate: string;
  status: 'WILL_CREATE' | 'CONFLICT_EXISTS' | 'POSITION_INACTIVE';
}

/** Matches `apps/api` `POST /calendar/copy/preview`. Never persists. */
export function previewCopyWeek(input: CopyWeekPreviewInput): Promise<CopyPlanRow[]> {
  return apiRequest<CopyPlanRow[]>('/calendar/copy/preview', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface CopyWeekApplyInput extends CopyWeekPreviewInput {
  /** Defaults to `false` server-side: conflicting rows are skipped, never overwritten silently. */
  overwrite?: boolean;
}

export interface ApplyCopyWeekResult {
  created: number;
  replaced: number;
  skipped: number;
}

/** Matches `apps/api` `POST /calendar/copy/apply`. Recomputes the plan server-side rather than trusting the client's preview. */
export function applyCopyWeek(input: CopyWeekApplyInput): Promise<ApplyCopyWeekResult> {
  return apiRequest<ApplyCopyWeekResult>('/calendar/copy/apply', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// --- Holidays (`apps/api` `HolidaysController`, PR 2) ---

export const ZONES = ['RIO_NEGRO', 'CALAFATE', 'VACA_MUERTA'] as const;
export type Zone = (typeof ZONES)[number];

export const ZONE_LABELS: Record<Zone, string> = {
  RIO_NEGRO: 'Río Negro',
  CALAFATE: 'Calafate',
  VACA_MUERTA: 'Vaca Muerta',
};

/** Matches `apps/api` `StoredHoliday` (`holiday.repository.ts`). `zone: null` is a global holiday. */
export interface Holiday {
  id: string;
  zone: Zone | null;
  date: string;
  name: string;
  hasService: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HolidaySearchParams {
  zone?: Zone | undefined;
  year?: number | undefined;
  page: number;
  pageSize: number;
}

export interface HolidaySearchResult {
  items: Holiday[];
  page: number;
  pageSize: number;
  total: number;
}

/** The largest page `GET /holidays` accepts; anything above is a 400 (`holiday.dto.ts`). */
export const MAX_HOLIDAY_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /holidays` (`holiday.dto.ts`'s `listHolidaysQuerySchema`). */
export function listHolidays(params: HolidaySearchParams): Promise<HolidaySearchResult> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });

  if (params.zone !== undefined) {
    query.set('zone', params.zone);
  }
  if (params.year !== undefined) {
    query.set('year', String(params.year));
  }

  return apiRequest<HolidaySearchResult>(`/holidays?${query.toString()}`);
}

/** Matches `apps/api` `createHolidaySchema`. `zone: null` creates a global holiday. */
export interface HolidayCreateInput {
  zone: Zone | null;
  date: string;
  name: string;
  hasService?: boolean;
  description?: string | null;
}

/** Matches `apps/api` `POST /holidays`. */
export function createHoliday(input: HolidayCreateInput): Promise<Holiday> {
  return apiRequest<Holiday>('/holidays', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Matches `apps/api` `updateHolidaySchema`: `zone`/`date` are intentionally
 * absent, both immutable once the holiday exists (`holidays/spec.md` →
 * "Edición de feriado").
 */
export interface HolidayUpdateInput {
  name?: string;
  hasService?: boolean;
  description?: string | null;
}

/** Matches `apps/api` `PATCH /holidays/:id`. */
export function updateHoliday(id: string, input: HolidayUpdateInput): Promise<Holiday> {
  return apiRequest<Holiday>(`/holidays/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

/** Matches `apps/api` `DELETE /holidays/:id`. */
export function deleteHoliday(id: string): Promise<void> {
  return apiRequest<void>(`/holidays/${id}`, { method: 'DELETE' });
}
